import type {
  MetaAccessConnectorAssets,
  MetaAccountAssetHolder,
  MetaAccountAssignment,
  MetaAccountInventory,
  MetaAccountUserHealth,
} from "~/ui-rtk/api/types/meta-account.types"
import { diceSimilarity, slug } from "~/ui-rtk/utils/name-similarity"
import { ASSIGNMENT_STATUS_LABEL, LOGIN_CHIP } from "./copy"

/** Pure helpers of Settings → Company → Meta access (APP-566 T2). */

export type AccessChipTone = "green" | "amber" | "gray" | "blue"

export interface AccessChipSpec {
  label: string
  tone: AccessChipTone
  title?: string
}

/** A login's health as a chip; none when it is fine. */
export function healthChipOf(
  health: MetaAccountUserHealth,
): AccessChipSpec | null {
  switch (health) {
    case "ok":
      return null
    case "paused":
      return { label: LOGIN_CHIP.paused, tone: "gray" }
    case "invalid":
      return { label: LOGIN_CHIP.notWorking, tone: "amber" }
    case "expiring":
      return { label: LOGIN_CHIP.expiresSoon, tone: "amber" }
    case "expired":
      return { label: LOGIN_CHIP.expired, tone: "amber" }
  }
}

const ASSIGNMENT_TONE: Record<MetaAccountAssignment["status"], AccessChipTone> =
  {
    active: "green",
    paused: "gray",
    lost_reach: "amber",
    login_paused: "gray",
    login_invalid: "amber",
    login_expired: "amber",
  }

/** The API already picked the status (first match wins); this only names it. */
export function assignmentStatusChipOf(
  assignment: Pick<MetaAccountAssignment, "status" | "lastError">,
): AccessChipSpec {
  const chip = {
    label: ASSIGNMENT_STATUS_LABEL[assignment.status],
    tone: ASSIGNMENT_TONE[assignment.status],
  }
  return assignment.status === "lost_reach" && assignment.lastError
    ? { ...chip, title: assignment.lastError }
    : chip
}

/** Warn this long before a login or its data access runs out. */
const EXPIRY_WARNING_MS = 14 * 24 * 60 * 60 * 1000

/** Amber chip when `iso` is past or within 14 days, else null. */
export function expiryWarningOf(
  iso: string | null,
  now: number = Date.now(),
): AccessChipSpec | null {
  if (!iso) return null
  const left = new Date(iso).getTime() - now
  if (left <= 0) return { label: LOGIN_CHIP.expired, tone: "amber" }
  return left <= EXPIRY_WARNING_MS
    ? { label: LOGIN_CHIP.expiresSoon, tone: "amber" }
    : null
}

/** A paused, not working or expired login cannot be assigned or moved to. */
export function isUsableLogin(health: MetaAccountUserHealth): boolean {
  return health === "ok" || health === "expiring"
}

/** "just now" / "9 minutes ago" / "3 hours ago" / "2 days ago". */
export function relativeTimeOf(iso: string, now: number = Date.now()): string {
  const seconds = Math.max(
    0,
    Math.floor((now - new Date(iso).getTime()) / 1000),
  )
  const unit = (n: number, word: string) =>
    `${n} ${word}${n === 1 ? "" : "s"} ago`
  if (seconds < 60) return "just now"
  const minutes = Math.floor(seconds / 60)
  if (minutes < 60) return unit(minutes, "minute")
  const hours = Math.floor(minutes / 60)
  if (hours < 24) return unit(hours, "hour")
  return unit(Math.floor(hours / 24), "day")
}

/** Whether `companyId` may pick an asset: nobody uses it, or the company itself does. */
export function isAvailableTo(
  heldBy: MetaAccountAssetHolder | null,
  companyId: string,
): boolean {
  return heldBy === null || heldBy.companyId === companyId
}

/** Ad accounts, Instagram accounts and Pages the login reaches that no company uses. */
export function unheldAssetCount(inventory: MetaAccountInventory): number {
  return [
    ...inventory.adAccounts,
    ...inventory.instagramAccounts,
    ...inventory.pages,
  ].filter(asset => asset.heldBy === null).length
}

export type CompanySuggestion =
  | { kind: "instagram"; igUserId: string; username: string | null }
  | { kind: "ad_account_binding"; adAccountId: string }
  | { kind: "ad_account_fivetran"; adAccountId: string }
  | { kind: "name"; igUserId: string; username: string | null; score: number }

/** A name match counts from this Dice score up. */
export const NAME_MATCH_MIN_SCORE = 0.6

interface SuggestionCompany {
  companyId: string
  companyName: string
  connector: MetaAccessConnectorAssets
}

/**
 * Which of the login's assets fits each company, from stored values only: an
 * exact id match with the company's connector (Instagram account, then the
 * ad-account binding, then the Fivetran list), else the best name match
 * (Dice on slugs, ≥ 0.6) between the company and an Instagram account's
 * username or its linked Page's name. No name match when the connector names
 * an Instagram account: the API refuses any other one. Only assets the
 * company may pick (nobody's, or its own). Companies without a suggestion are
 * left out.
 */
export function suggestionsFor(
  inventory: MetaAccountInventory,
  companies: SuggestionCompany[],
): Map<string, CompanySuggestion> {
  const pageNames = new Map(inventory.pages.map(page => [page.id, page.name]))
  const suggestions = new Map<string, CompanySuggestion>()
  for (const { companyId, companyName, connector } of companies) {
    const instagram = inventory.instagramAccounts.filter(ig =>
      isAvailableTo(ig.heldBy, companyId),
    )
    const adAccounts = inventory.adAccounts.filter(account =>
      isAvailableTo(account.heldBy, companyId),
    )

    const exactInstagram = connector.igUserId
      ? instagram.find(ig => ig.id === connector.igUserId)
      : undefined
    if (exactInstagram) {
      suggestions.set(companyId, {
        kind: "instagram",
        igUserId: exactInstagram.id,
        username: exactInstagram.username,
      })
      continue
    }
    const bound = connector.adAccountId
      ? adAccounts.find(account => account.id === connector.adAccountId)
      : undefined
    if (bound) {
      suggestions.set(companyId, {
        kind: "ad_account_binding",
        adAccountId: bound.id,
      })
      continue
    }
    const synced = adAccounts.find(account =>
      connector.fivetranFbAccountIds.includes(account.id),
    )
    if (synced) {
      suggestions.set(companyId, {
        kind: "ad_account_fivetran",
        adAccountId: synced.id,
      })
      continue
    }
    // The connector's own Instagram account is the only one the API accepts.
    if (connector.igUserId) continue

    const companySlug = slug(companyName)
    let best: { ig: (typeof instagram)[number]; score: number } | null = null
    for (const ig of instagram) {
      const pageName = ig.pageId ? pageNames.get(ig.pageId) : null
      const score = Math.max(
        diceSimilarity(companySlug, slug(ig.username)),
        diceSimilarity(companySlug, slug(pageName)),
      )
      if (score >= NAME_MATCH_MIN_SCORE && (!best || score > best.score)) {
        best = { ig, score }
      }
    }
    if (best) {
      suggestions.set(companyId, {
        kind: "name",
        igUserId: best.ig.id,
        username: best.ig.username,
        score: best.score,
      })
    }
  }
  return suggestions
}
